import React, { useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import useEmblaCarousel from 'embla-carousel-react'
import { useDotButton } from './EmblaCarouselDotButton'
import './image-lightbox.css'

/**
 * Visualizador de fotos em ecrã inteiro.
 *
 * Abre a partir da galeria de um serviço para que a cliente possa ver o
 * trabalho ao pormenor (unhas, pestanas, sobrancelhas) sem o recorte do cartão.
 * Setas do teclado navegam, Esc fecha, e em telemóvel arrasta-se com o dedo.
 */
const ImageLightbox = ({ media = [], title = '', startIndex = 0, onClose }) => {
  const hasMany = media.length > 1

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: hasMany,
    startIndex,
    align: 'center',
    duration: 22,
  })

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi)

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose?.()
      }
      if (e.key === 'ArrowLeft') emblaApi?.scrollPrev()
      if (e.key === 'ArrowRight') emblaApi?.scrollNext()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [emblaApi, onClose])

  if (!media.length) return null

  const current = media[selectedIndex] || media[0]

  return createPortal(
    <div className="lb" role="dialog" aria-modal="true" aria-label={`Fotografias — ${title}`} onClick={onClose}>
      <header className="lb__bar" onClick={(e) => e.stopPropagation()}>
        <span className="lb__title">{title}</span>
        <div className="lb__bar-right">
          {hasMany && (
            <span className="lb__counter">
              {selectedIndex + 1} / {media.length}
            </span>
          )}
          <button type="button" className="lb__close" onClick={onClose} aria-label="Fechar fotografias">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </header>

      <div className="lb__viewport" ref={emblaRef} onClick={(e) => e.stopPropagation()}>
        <div className="lb__container">
          {media.map((item, index) => (
            <div className="lb__slide" key={index}>
              {item.type === 'compare' ? (
                <div className="lb__compare">
                  <figure className="lb__compare-pane">
                    <img className="lb__img" src={item.before} alt={`${item.alt} — antes`} draggable={false} />
                    <figcaption className="lb__compare-label">Antes</figcaption>
                  </figure>
                  <figure className="lb__compare-pane">
                    <img className="lb__img" src={item.after} alt={`${item.alt} — depois`} draggable={false} />
                    <figcaption className="lb__compare-label lb__compare-label--after">Depois</figcaption>
                  </figure>
                </div>
              ) : (
                <img className="lb__img" src={item.src} alt={item.alt || title} draggable={false} />
              )}
            </div>
          ))}
        </div>
      </div>

      {hasMany && (
        <>
          <button
            type="button"
            className="lb__arrow lb__arrow--prev"
            onClick={(e) => {
              e.stopPropagation()
              scrollPrev()
            }}
            aria-label="Foto anterior"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 5 8 12l7 7" />
            </svg>
          </button>
          <button
            type="button"
            className="lb__arrow lb__arrow--next"
            onClick={(e) => {
              e.stopPropagation()
              scrollNext()
            }}
            aria-label="Foto seguinte"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="lb__dots" onClick={(e) => e.stopPropagation()}>
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`lb__dot${index === selectedIndex ? ' lb__dot--active' : ''}`}
                onClick={() => onDotButtonClick(index)}
                aria-label={`Ir para a foto ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {current?.type === 'compare' && (
        <p className="lb__hint" onClick={(e) => e.stopPropagation()}>
          Resultado real de uma cliente — antes e depois do tratamento
        </p>
      )}
    </div>,
    document.body
  )
}

export default ImageLightbox
